import {
  DEFAULT_OUTPUT_PROFILE,
  OUTPUT_PROFILES,
  outputProfileLabel,
  selectableOutputProfile,
  outputProfileOf,
  type OutputProfile,
} from '@/core/outputProfile';
import { SOLE_TRADER_CLIENT_TYPE, useClientType } from '@/core/useClientType';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import {
  SIGNATORY_ROLES,
  SIGNATORY_ROLE_LABELS,
  roleRequiresRelationship,
  type ApprovalState,
  type DocumentVersionRow,
  type MissingSignatory,
  type SignatoriesView,
  type SignatoryCandidate,
} from '@/core/models/approval.models';
import { toIsoDate, type PeriodRow } from '@/core/models/practice.models';
import { errorMessage, notifyError, notifySuccess } from '@/core/notify/notify';
import { rememberChosenPeriod, resolveDefaultPeriodId } from '@/core/periodDefault';
import { approvalService } from '@/core/services/approval';
import { practiceService } from '@/core/services/practice';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { DateField, type DateValue } from '@/kit/DateField';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { SectionTabs } from '@/kit/SectionTabs';
import { openDialog } from '@/kit/dialog';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import {
  candidateLists,
  candidatesForRole,
  defaultDocumentId,
  documentsOfProfile,
  documentLabel,
  fillMissingRows,
  nextStage,
  readyVersionLabel,
  reportSignDates,
  signatureDateOf,
  sortDocuments,
  statusLabel,
  unsignedCount,
  type CandidateLists,
  type SignatoryRow,
} from './approvalLogic';
import { AccountingPeriodSelect, currentPeriod, displayDate, useClientGuard, useLatest } from './shared';
import { UnfinaliseDialog, type UnfinaliseDialogData } from './UnfinaliseDialog';
import './TaxreturnApproval.scss';

const NO_LISTS: CandidateLists = { officers: [], secretaries: [] };

/** APT352: the codes finalising answers with when it needs the validations first (AP-VAL-002: none run, or the figures changed since) or they hold blocking issues (AP-VAL-001). */
const VALIDATION_NOT_RUN = 'AP-VAL-002';
const VALIDATION_BLOCKING = 'AP-VAL-001';
const VALIDATION_CENTRE_ROUTE = '/home/taxreturns/validation-centre';

/** What a refused Finalise asks the user to do next: run the validations here, and/or open the Validation Centre. */
interface ValidationPrompt {
  message: string;
  canRun: boolean;
}

/**
 * "Approval & Signatories": the accounts workflow of one accounting period (stage, versions, readiness for the client,
 * client approval, finalising) and the signatories table. The screen only reflects state; the API enforces the locks.
 */
export function TaxreturnApproval() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const client = useClientGuard();

  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);

  const [state, setState] = useState<ApprovalState | null>(null);
  const [missing, setMissing] = useState<MissingSignatory[]>([]);
  const [rows, setRows] = useState<SignatoryRow[]>([]);
  /** #145: the period's generated accounts versions, chosen from a list instead of typing an internal id. */
  const [documents, setDocuments] = useState<DocumentVersionRow[]>([]);
  /** Full, Abridged and Filleted accounts are generated, approved and finalised separately (one set at a time). */
  // A submission's "Finalise the accounts" link names the accounts type it files (`&profile=ABRIDGED`), Full otherwise.
  // APT341: a link that names the retired Abridged accounts opens on the Full accounts instead (Abridged can no longer be finalised or filed).
  const [outputProfile, setOutputProfile] = useState<OutputProfile>(() =>
    selectableOutputProfile(outputProfileOf({ outputProfile: searchParams.get('profile') })),
  );
  const [clientApproverName, setClientApproverName] = useState('');
  const [lists, setLists] = useState<CandidateLists>(NO_LISTS);
  const [documentVersionId, setDocumentVersionId] = useState('');
  const [clearReason, setClearReason] = useState('');
  /** A save, send or approval request is running (stops a second click). */
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [allowOpenReviewPoints, setAllowOpenReviewPoints] = useState(false);
  const [validationPrompt, setValidationPrompt] = useState<ValidationPrompt | null>(null);
  /**
   * 5 Nov design review (approval-suggestion-3-tabbed-sections.html): the page used to be one long scroll mixing
   * status, the version/stage workflow, client approval and the signatories table. Split into three tabs instead.
   * 6 Nov: Overview is the default, not Workflow - "what state are these accounts in, and what's still needed"
   * is what a preparer opening this screen wants to see first, before deciding which version action to take.
   */
  const [activeTab, setActiveTab] = useState<'overview' | 'workflow' | 'signatories'>('overview');
  /** A sole trader signs as the proprietor rather than a director. */
  const isSoleTrader = useClientType(client?.id) === SOLE_TRADER_CLIENT_TYPE;
  // The API stores the role as DIRECTOR (its only owner-level role); only the label changes.
  const roleLabel = (role: string) =>
    isSoleTrader && role === 'DIRECTOR' ? 'Proprietor' : SIGNATORY_ROLE_LABELS[role];

  const rowKey = useRef(0);
  const newKey = () => ++rowKey.current;
  const latest = useLatest({ rows, missing, lists, selectedPeriodId, outputProfile });

  const applySignatories = (view: SignatoriesView) => {
    const list = candidateLists(view.candidates ?? []);
    setLists(list);
    setMissing(view.missing ?? []);
    setRows(
      (view.signatories ?? []).map((s) => ({
        key: newKey(),
        signatoryId: s.signatoryId,
        reportRole: s.reportRole,
        reportContactId: s.reportContactId,
        personName: s.personName,
        signatureDate: signatureDateOf(s.signatureDate),
      })),
    );
  };

  const load = (periodId: string | null) => {
    if (!periodId) {
      return;
    }
    approvalService.getPeriodDocuments(periodId).then(
      (result) => {
        const sorted = sortDocuments(result);
        setDocuments(sorted);
        setDocumentVersionId((current) =>
          defaultDocumentId(documentsOfProfile(sorted, latest.current.outputProfile), current),
        );
      },
      () => setDocuments([]),
    );
    approvalService.getState(periodId).then(
      (result) => {
        setState(result);
        approvalService.getSignatories(periodId).then(applySignatories, (error) => notifyError(error));
      },
      (error) => notifyError(error),
    );
  };

  useEffect(() => {
    if (!client) {
      return;
    }
    practiceService.getClientPeriods(client.id).then(
      async (response) => {
        const list = response.periods ?? [];
        setPeriods(list);
        // #172: opened from a submission's "Finalise the accounts" issue, on the period being submitted.
        const requested = searchParams.get('periodId');
        const id =
          list.find((p) => p.id === requested)?.id ??
          (await resolveDefaultPeriodId(client.id, list)) ??
          currentPeriod(list)?.id ??
          null;
        setSelectedPeriodId(id);
        if (id) {
          load(id);
        } else {
          notifyError('Add an accounting period for this client first.');
        }
      },
      (error) => notifyError(error),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const onPeriodChange = (periodId: string) => {
    setSelectedPeriodId(periodId);
    setValidationPrompt(null);
    load(periodId);
  };

  const stage = nextStage(state?.periodStatus);
  const profileDocuments = documentsOfProfile(documents, isSoleTrader ? DEFAULT_OUTPUT_PROFILE : outputProfile);

  const changeOutputProfile = (profile: OutputProfile) => {
    setOutputProfile(profile);
    setDocumentVersionId(defaultDocumentId(documentsOfProfile(documents, profile), ''));
  };

  const selectedDocument = documents.find((d) => d.documentVersionId === documentVersionId);

  /**
   * An approved version can be finalised while the period is Approved - and also once it is Finalised (or Filed), for a kind
   * of accounts not finalised yet: finalising the Full accounts does not finalise the Abridged ones a submission files, and the
   * API accepts that "additional profile". A kind that already has its final version is a revision, not offered here.
   */
  const canFinalise =
    selectedDocument?.status === 3 &&
    (state?.periodStatus === 4 ||
      ((state?.periodStatus === 5 || state?.periodStatus === 6) && !profileDocuments.some((d) => d.status === 4)));

  /** True when no such request is running; marks one as running until `finish`. */
  const begin = () => {
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    return true;
  };
  const finish = () => {
    savingRef.current = false;
    setSaving(false);
  };

  /** Ready for client approval locks the accounting and disclosure inputs. */
  const setReady = () => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (!documentVersionId) {
      notifyError('Select the approved accounts version being sent to the client.');
      return;
    }
    if (!begin()) {
      return;
    }
    approvalService
      .setReady(periodId, { documentVersionId, allowOpenReviewPoints })
      .then(
        (result) => {
          setState(result);
          notifySuccess('These accounts are now with the client for approval.');
          load(periodId);
        },
        (error) => notifyError(error),
      )
      .finally(finish);
  };

  /** Clearing readiness invalidates the approval and every signature, in one transaction. */
  const clearReady = () => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (!begin()) {
      return;
    }
    approvalService
      .clearReady(periodId, { reason: clearReason || null })
      .then(
        (result) => {
          setState(result.state);
          setClearReason('');
          notifySuccess(
            result.signatoriesInvalidated > 0
              ? `Approval cleared. ${result.signatoriesInvalidated} signature(s) were invalidated and must be re-signed.`
              : 'Approval cleared.',
          );
          load(periodId);
        },
        (error) => notifyError(error),
      )
      .finally(finish);
  };

  const runStep = (step: Promise<unknown>, success: string, periodId: string) => {
    step.then(
      () => {
        notifySuccess(success);
        load(periodId);
      },
      (error) => notifyError(error),
    );
  };

  const moveToNextStage = () => {
    const periodId = selectedPeriodId;
    if (!periodId || !stage) {
      return;
    }
    runStep(
      approvalService.changePeriodStatus(periodId, stage.status),
      `The accounts are now ${statusLabel(stage.status).toLowerCase()}.`,
      periodId,
    );
  };

  const generateVersion = () => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      return;
    }
    approvalService.generateDocument(periodId, outputProfile).then(
      (row) => {
        setDocumentVersionId(row.documentVersionId);
        notifySuccess(`Accounts version ${row.versionNo} generated.`);
        load(periodId);
      },
      (error) => notifyError(error),
    );
  };

  const approveVersion = () => {
    const periodId = selectedPeriodId;
    if (!periodId || !documentVersionId) {
      notifyError('Select the accounts version to approve.');
      return;
    }
    runStep(approvalService.approveDocument(periodId, documentVersionId), 'Accounts version approved.', periodId);
  };

  const finaliseAccounts = () => {
    const periodId = selectedPeriodId;
    if (!periodId || !documentVersionId) {
      notifyError('Select the approved accounts version to finalise.');
      return;
    }
    setValidationPrompt(null);
    approvalService.finalisePeriod(periodId, documentVersionId).then(
      () => {
        notifySuccess('The accounts are finalised and can now be submitted to Companies House.');
        load(periodId);
      },
      (error) => {
        // APT352: a refusal that needs the validations is a step to take here, not a message to read and go looking for.
        const code = error?.error?.code;
        if (code === VALIDATION_NOT_RUN || code === VALIDATION_BLOCKING) {
          setValidationPrompt({ message: errorMessage(error), canRun: code === VALIDATION_NOT_RUN });
          return;
        }
        notifyError(error);
      },
    );
  };

  /**
   * APT336 (the client: "how to revert the accounts from Finalised back to Approved"): finalised accounts go back to Approved, with a reason the user gives. The API
   * refuses accounts that were submitted or filed, or that have a revision, and says why.
   */
  const unfinaliseAccounts = () => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      return;
    }
    const period = periods.find((p) => p.id === periodId);
    setValidationPrompt(null);
    openDialog<UnfinaliseDialogData, string>(UnfinaliseDialog, {
      panelClass: 'cst-invite',
      width: '560px',
      maxWidth: '94vw',
      data: { periodLabel: period ? `${displayDate(period.fromDate)} to ${displayDate(period.toDate)}` : '' },
    })
      .afterClosed()
      .then((reason) => {
        if (!reason) {
          return;
        }
        approvalService.unfinalisePeriod(periodId, reason).then(
          () => {
            notifySuccess('The accounts are back to Approved. Change what you need, then approve and finalise them again.');
            load(periodId);
          },
          (error) => notifyError(error),
        );
      });
  };

  /** Runs the validations on the current figures, which is what finalising checks, then asks to finalise again. */
  const runValidations = () => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      return;
    }
    approvalService.runValidations(periodId).then(
      (run) => {
        if (run.blockingCount > 0) {
          setValidationPrompt({
            message: `${run.blockingCount} blocking validation issue(s) must be cleared before finalising. Open the Validation Centre to see them.`,
            canRun: false,
          });
          return;
        }
        setValidationPrompt(null);
        notifySuccess(`Validations run: no blocking issues (${run.warningCount} warning(s)). Select Finalise accounts again.`);
      },
      (error) => notifyError(error),
    );
  };

  const openValidationCentre = () => {
    if (selectedPeriodId) {
      rememberChosenPeriod(selectedPeriodId);
    }
    navigate(`${VALIDATION_CENTRE_ROUTE}?profile=${outputProfile}`);
  };

  const recordClientApproval = (name: string) => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      return;
    }
    if (!begin()) {
      return;
    }
    approvalService
      .recordApproval(periodId, { approvalType: 'Client', approvedName: name || null })
      .then(
        (result) => {
          setState(result);
          notifySuccess(NotificationTextMessage.successMessage);
        },
        (error) => notifyError(error),
      )
      .finally(finish);
  };

  // ---- Signatories ----

  /** The only person who can sign in this role (a lone director or proprietor), chosen for the preparer; else none. */
  const onlyCandidate = (role: string): string | null => {
    const candidates = candidatesForRole(role, latest.current.lists);
    return candidates.length === 1 ? candidates[0].reportContactId : null;
  };

  const addRow = () =>
    setRows((current) => [
      ...current,
      {
        key: newKey(),
        signatoryId: null,
        reportRole: 'DIRECTOR',
        reportContactId: onlyCandidate('DIRECTOR'),
        personName: null,
        signatureDate: null,
      },
    ]);

  const updateRow = (key: number, patch: Partial<SignatoryRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const removeRow = (index: number) => setRows((current) => current.filter((_, i) => i !== index));

  /**
   * #143: adds a row for each signature the period still needs, so the preparer only checks and saves. Nothing is
   * saved until the preparer clicks Save.
   */
  const addMissingSignatories = () => {
    const periodId = selectedPeriodId;
    if (!periodId || !client || !missing.length) {
      return;
    }
    reportSignDates(client.id, periodId).then((dates) => {
      const now = latest.current;
      const result = fillMissingRows(now.rows, now.missing, dates, now.lists, newKey);
      setRows(result.rows);
      if (result.changed > 0) {
        notifySuccess('Signatories added below. Check the person and signature date on each, then click Save.');
        // The rows just added live in the Signatories tab, not this (Overview) one - follow them there.
        setActiveTab('signatories');
      }
    });
  };

  const saveSignatories = () => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    const signatories = rows.map((r) => ({
      signatoryId: r.signatoryId,
      reportRole: r.reportRole,
      reportContactId: roleRequiresRelationship(r.reportRole) ? r.reportContactId : null,
      personName: r.personName,
      signatureDate: toIsoDate(r.signatureDate),
    }));
    if (!begin()) {
      return;
    }
    approvalService
      .saveSignatories(periodId, { revisionCaseId: null, signatories, removeMissing: true })
      .then(
        (view) => {
          applySignatories(view);
          notifySuccess(NotificationTextMessage.successMessage);
          load(periodId);
        },
        (error) => notifyError(error),
      )
      .finally(finish);
  };

  const signatoryColumns: DataTableColumn<SignatoryRow>[] = [
    {
      id: 'role',
      header: 'Role',
      headerClassName: 'w-150',
      cell: (row) => (
        <Select<string>
          items={SIGNATORY_ROLES}
          clearable={false}
          searchable={false}
          value={row.reportRole}
          onChange={(value) => {
            // A role with a single possible signatory gets them chosen; otherwise the person stays as it was.
            const only = onlyCandidate(value as string);
            updateRow(row.key, { reportRole: value as string, ...(only ? { reportContactId: only } : {}) });
          }}
          renderLabel={roleLabel}
          renderOption={roleLabel}
        />
      ),
    },
    {
      id: 'person',
      header: 'Person',
      cell: (row) =>
        // Directors, members, partners and secretaries must be real relationships
        roleRequiresRelationship(row.reportRole) ? (
          <Select<SignatoryCandidate>
            items={candidatesForRole(row.reportRole, lists)}
            bindValue="reportContactId"
            bindLabel="fullName"
            placeholder="Select a person"
            value={row.reportContactId}
            onChange={(value) => updateRow(row.key, { reportContactId: (value as string | null) ?? null })}
          />
        ) : (
          <InputField
            placeholder="Accountant name"
            maxLength={255}
            charMode="text"
            value={row.personName ?? ''}
            onChange={(event) => updateRow(row.key, { personName: event.target.value })}
          />
        ),
    },
    {
      id: 'date',
      header: 'Signature date',
      headerClassName: 'w-180',
      cell: (row) => (
        <DateField
          value={row.signatureDate as DateValue}
          onChange={(value) => updateRow(row.key, { signatureDate: value })}
          placeholder="dd/mm/yyyy"
        />
      ),
    },
    {
      id: 'action',
      header: 'Action',
      headerClassName: 'w-60',
      cell: (_row, index) => (
        <button
          type="button"
          className="mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base"
          aria-label="Remove"
          onClick={() => removeRow(index)}
        >
          <Icon name="delete_outline" outlined />
        </button>
      ),
    },
  ];

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-approval">
      <div className="sidenav-details">
        {/* 5 Nov design review: the side menu's own "Approval & Signatories" item already names this page, and it
            sits in the normal page layout (not a slide-over panel), so the heading repeated it and the close (X)
            button had nothing to close - the side menu itself is how a user leaves this screen, same as every
            other page (same clean-up as Review Points and Working Papers). */}
        <div className="sidenav-body">
          {/* 6 Nov design review: the Overview/Workflow/Signatories tab strip sits above the Accounting period
              filter bar, same as Accounts Builder's own output tabs above its Report/Period filter row - not the
              other way round. */}
          {state && (
            <SectionTabs
              ariaLabel="Approval & Signatories sections"
              value={activeTab}
              onChange={(id) => setActiveTab(id as 'overview' | 'workflow' | 'signatories')}
              tabs={[
                { id: 'overview', label: 'Overview', badge: missing.length || undefined },
                { id: 'workflow', label: 'Workflow' },
                { id: 'signatories', label: 'Signatories', badge: rows.length || undefined },
              ]}
            >
              {(active) => (
                <>
                  {/* The same white, bordered content-header bar as Statutory Disclosures, Validation Centre and
                      Accounts Builder's own filter rows - sits right under the tab strip, above whichever tab's
                      content is active, instead of plain text loose on the page background. */}
                  <div className="content-header mb-0">
                    <div className="content-header-left mb-1 d-flex align-items-center">
                      <span className="pr-1">Accounting period</span>
                      <AccountingPeriodSelect periods={periods} value={selectedPeriodId} onChange={onPeriodChange} />
                    </div>
                    <div className="content-header-right mb-1">
                      <span className="status-chip">{statusLabel(state.periodStatus)}</span>
                    </div>
                  </div>

                  {active === 'overview' && (
                    <>
                      {/* Readiness banner: this is the lock the rest of the module respects */}
                      <div className={`alert-message ${state.isReadyForApproval ? 'alert-warning' : 'alert-info'}`}>
                        <Icon name={state.isReadyForApproval ? 'lock' : 'lock_open'} outlined />
                        {state.isReadyForApproval ? (
                          <span>
                            These accounts are with the client for approval. The accounting and disclosure inputs are
                            read-only until the readiness is cleared.
                          </span>
                        ) : (
                          <span>
                            These accounts are not with the client. The accounting and disclosure inputs can be
                            edited.
                          </span>
                        )}
                      </div>

                      {state.isStale && (
                        <div className="alert-message alert-danger">
                          <Icon name="warning" outlined />
                          The figures have changed since these accounts were sent for approval. Clear the readiness,
                          regenerate the accounts and send them again.
                        </div>
                      )}

                      {/* #143: the signatures are the approval record between the practice and the client. They do
                          not block submitting the accounts (only a revision is held until it is signed), so this is
                          not an error. */}
                      {missing.length > 0 && (
                        <div className="alert-message alert-info">
                          <Icon name="draw" outlined />
                          <div>
                            <div>Signatures still to record for the approval record:</div>
                            <ul className="mb-0">
                              {missing.map((m, index) => (
                                <li key={`${m.reportRole}-${index}`}>{m.description}</li>
                              ))}
                            </ul>
                            <div className="pt-1">
                              Each one is a signatory with a signature date in the Signatories table below. The sign
                              dates on the accounts report form print on the accounts but are not recorded here, and
                              signatures are set aside when the figures change after signing, so they are then
                              recorded again for the accounts being approved.
                            </div>
                            <Button
                              className="action-button secondary-button border-radius-6 mt-1"
                              onClick={addMissingSignatories}
                            >
                              <Icon name="person_add" />
                              <span>Add missing signatories</span>
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Current approval state */}
                      <div className="mat-table-wrapper">
                        <table className="detail-table">
                          <tbody>
                            <tr>
                              <th>Ready for client approval</th>
                              <td>{state.isReadyForApproval ? 'Yes' : 'No'}</td>
                              <th>Approved version</th>
                              <td>{readyVersionLabel(state, documents)}</td>
                            </tr>
                            <tr>
                              <th>Sent for approval</th>
                              <td>{displayDate(state.readyAtUtc) || '-'}</td>
                              <th>Client approved</th>
                              <td>{displayDate(state.clientApprovedAtUtc) || 'Not yet'}</td>
                            </tr>
                            <tr>
                              <th>Open review points</th>
                              <td>{state.openReviewPoints}</td>
                              <th>Times cleared</th>
                              <td>{state.clearedCount}</td>
                            </tr>
                            {state.lastClearedReason && (
                              <tr>
                                <th>Last cleared because</th>
                                <td colSpan={3}>{state.lastClearedReason}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {active === 'workflow' && (
                    <>
                      {/* #143-#146, #172: the accounts workflow. */}
                      <div className="form-field-title pt-1 pb-1">
                        <h3 className="mb-1">Accounts workflow</h3>
                        <div className="d-flex align-items-center gap-15 flex-wrap">
                          <span>
                            Stage: <strong>{statusLabel(state.periodStatus)}</strong>
                          </span>
                          {stage && (
                            <Button className="action-button secondary-button border-radius-6" onClick={moveToNextStage}>
                              <Icon name="arrow_forward" />
                              <span>{stage.label}</span>
                            </Button>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-15 flex-wrap pt-1">
                          {!isSoleTrader && (
                            <Select<(typeof OUTPUT_PROFILES)[number]>
                              className="w-200"
                              items={OUTPUT_PROFILES}
                              bindLabel="label"
                              bindValue="code"
                              clearable={false}
                              searchable={false}
                              value={outputProfile}
                              onChange={(value) =>
                                changeOutputProfile((value as OutputProfile | null) ?? DEFAULT_OUTPUT_PROFILE)
                              }
                            />
                          )}
                          <Select<DocumentVersionRow>
                            className="w-350"
                            items={profileDocuments}
                            bindValue="documentVersionId"
                            clearable={false}
                            searchable={false}
                            placeholder={
                              outputProfile === DEFAULT_OUTPUT_PROFILE
                                ? 'No accounts version generated yet'
                                : `No ${outputProfileLabel(outputProfile).toLowerCase()} version generated yet`
                            }
                            value={documentVersionId}
                            onChange={(value) => setDocumentVersionId((value as string | null) ?? '')}
                            renderLabel={(item) => documentLabel(item)}
                            renderOption={(item) => documentLabel(item)}
                          />
                          <Button className="action-button secondary-button border-radius-6" onClick={generateVersion}>
                            <Icon name="autorenew" />
                            <span>Generate accounts version</span>
                          </Button>
                          {selectedDocument?.status === 2 && (
                            <Button className="action-button secondary-button border-radius-6" onClick={approveVersion}>
                              <Icon name="verified" />
                              <span>Approve this version</span>
                            </Button>
                          )}
                          {canFinalise && (
                            <Button className="action-button primary-button border-radius-6" onClick={finaliseAccounts}>
                              <Icon name="task_alt" />
                              <span>Finalise accounts</span>
                            </Button>
                          )}
                          {state.periodStatus === 5 && (
                            <Button className="action-button secondary-button border-radius-6" onClick={unfinaliseAccounts}>
                              <Icon name="undo" />
                              <span>Unfinalise accounts</span>
                            </Button>
                          )}
                        </div>
                        {validationPrompt && (
                          <div className="alert-message alert-danger" role="alert">
                            <Icon name="warning" outlined />
                            <div>
                              <div>{validationPrompt.message}</div>
                              <div className="d-flex align-items-center gap-15 flex-wrap pt-1">
                                {validationPrompt.canRun && (
                                  <Button className="action-button primary-button border-radius-6" onClick={runValidations}>
                                    <Icon name="rule" />
                                    <span>Run validations</span>
                                  </Button>
                                )}
                                <Button className="action-button secondary-button border-radius-6" onClick={openValidationCentre}>
                                  <Icon name="fact_check" />
                                  <span>Open Validation Centre</span>
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        {state.periodStatus === 5 && (
                          <div className="text-muted pt-1" data-testid="finalised-note">
                            The accounts are finalised. Generating another version does not change this stage. To change the accounts, unfinalise them (they go back to
                            Approved); accounts that have been submitted or filed are changed by a revision.
                          </div>
                        )}
                        {state.periodStatus === 6 && (
                          <div className="text-muted pt-1" data-testid="filed-note">
                            These accounts have been filed, so they cannot be unfinalised. A filed set of accounts is changed by a revision.
                          </div>
                        )}
                        <div className="text-muted pt-1">
                          Move the accounts to Under review, generate and approve a version, send it for client approval and
                          record the approval, then approve the accounts and finalise them. Finalised accounts can be submitted
                          from Submit.
                        </div>
                        {state.isReadyForApproval && !state.clientApprovedAtUtc && (
                          <div className="d-flex align-items-center gap-15 flex-wrap pt-1">
                            <InputField
                              fieldClassName="w-350"
                              placeholder="Name of the person approving for the client"
                              maxLength={255}
                              charMode="text"
                              value={clientApproverName}
                              onChange={(event) => setClientApproverName(event.target.value)}
                            />
                            <Button
                              className="action-button primary-button border-radius-6"
                              busy={saving}
                              onClick={() => recordClientApproval(clientApproverName)}
                            >
                              <Icon name="how_to_reg" />
                              <span>Record client approval</span>
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Set ready / clear ready */}
                      {!state.isReadyForApproval && (
                        <div className="form-field-title pt-1 pb-1">
                          <div className="d-flex align-items-center">
                            <span className="text-muted pr-1">Send the selected version to the client:</span>
                            <Checkbox
                              hostClassName="pl-1"
                              checked={allowOpenReviewPoints}
                              onChange={(event) => setAllowOpenReviewPoints(event.target.checked)}
                            >
                              Send with review points still open
                            </Checkbox>
                            <Button className="action-button primary-button border-radius-6 ml-1" busy={saving} onClick={setReady}>
                              <Icon name="send" />
                              <span>Ready for client approval</span>
                            </Button>
                          </div>
                        </div>
                      )}

                      {state.isReadyForApproval && (
                        <div className="form-field-title pt-1 pb-1">
                          <div className="d-flex align-items-center flex-wrap">
                            <InputField
                              fieldClassName="w-350"
                              placeholder="Reason for clearing"
                              maxLength={500}
                              value={clearReason}
                              onChange={(event) => setClearReason(event.target.value)}
                            />
                            <Button className="action-button secondary-button border-radius-6 ml-1" busy={saving} onClick={clearReady}>
                              <Icon name="lock_open" />
                              <span>Clear readiness</span>
                            </Button>
                          </div>
                          <div className="text-muted pt-1">
                            Clearing readiness invalidates the client approval and every signature date; the accounts must be
                            re-approved and re-signed.
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {active === 'signatories' && (
                    <>
                      <div className="form-field-title pt-1 pb-1 d-flex align-items-center justify-space-between">
                        <h3 className="mb-0">Signatories</h3>
                        <Button className="action-button primary-button border-radius-6" onClick={addRow}>
                          <Icon name="add" />
                          <span>Add Signatory</span>
                        </Button>
                      </div>

                      <div className="mat-table-wrapper">
                        <DataTable columns={signatoryColumns} rows={rows} rowKey={(row) => row.key} />
                        {!rows.length && <div className="text-muted pt-1">No signatories recorded yet.</div>}
                      </div>

                      {unsignedCount(rows) > 0 && rows.length > 0 && (
                        <div className="alert-message alert-info mt-1">
                          <Icon name="draw" outlined />
                          <span>
                            {unsignedCount(rows) === 1
                              ? '1 signatory has no signature date and needs to re-sign.'
                              : `${unsignedCount(rows)} signatories have no signature date and need to re-sign.`}{' '}
                            Enter the date each person signs the accounts, then click Save Signatories.
                          </span>
                        </div>
                      )}

                      <div className="pt-1">
                        <Button className="action-button primary-button border-radius-6" busy={saving} onClick={saveSignatories}>
                          <span>Save Signatories</span>
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </SectionTabs>
          )}
          {/* Fallback for when there is no state yet to show tabs for (still loading, or the state request
              failed) - the period picker must stay reachable so the user can pick another period and recover,
              rather than disappearing along with the tabs. */}
          {!state && (
            <div className="content-header mb-0">
              <div className="content-header-left mb-1 d-flex align-items-center">
                <span className="pr-1">Accounting period</span>
                <AccountingPeriodSelect periods={periods} value={selectedPeriodId} onChange={onPeriodChange} />
              </div>
            </div>
          )}
        </div>
      </div>
    </SideMenuPage>
  );
}
